
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash } from "lucide-react";
import { Tables } from "@/integrations/supabase/types";

type ActivityCategory = Tables<"activity_categories">;

interface ActivityCategoryCardProps {
  category: ActivityCategory;
  onEdit: (category: ActivityCategory) => void;
  onDelete: (categoryId: string) => void;
}

export const ActivityCategoryCard = ({ category, onEdit, onDelete }: ActivityCategoryCardProps) => {
  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete the activity category "${category.name}"?`)) {
      onDelete(category.id);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "published":
        return "bg-green-100 text-green-800";
      case "draft":
        return "bg-yellow-100 text-yellow-800";
      case "archived":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg flex items-center space-x-2">
          <div
            className="w-4 h-4 rounded-full"
            style={{ backgroundColor: category.color || "#3B82F6" }}
          />
          <span>{category.name}</span>
          {category.icon && (
            <span className="text-sm text-gray-500">({category.icon})</span>
          )}
        </CardTitle>
        <div className="flex space-x-2">
          <Button variant="outline" size="sm" onClick={() => onEdit(category)}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="destructive" size="sm" onClick={handleDelete}>
            <Trash className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {category.description && (
            <p className="text-sm text-gray-600">{category.description}</p>
          )}
          <div className="flex items-center justify-between">
            <Badge className={getStatusColor(category.status || "draft")}>
              {category.status || "draft"}
            </Badge>
            <span className="text-sm text-gray-500">
              Order: {category.display_order || 0}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
